import React from 'react';
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ExpandMore, FileCopy } from '@material-ui/icons';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_TASKS_WITH_FIELDS } from '../get-tasks-with-fields.gql';

const useStyles = makeStyles((theme: any) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  heading: {
    fontSize: '16px',
    fontWeight: 600,
  },
  taskTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  taskId: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  fieldName: {
    fontSize: '13px',
    fontWeight: 500,
  },
  fieldId: {
    fontSize: '11px',
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
  },
  optionsChip: {
    margin: theme.spacing(0.5),
    fontSize: '11px',
  },
  copyButton: {
    padding: 4,
  },
}));

export function TaskFieldsReference() {
  const classes = useStyles();
  const { data, loading } = useCampfireQuery(GET_TASKS_WITH_FIELDS, {});

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getFieldTypeLabel = (typename: string): string => {
    const typeMap: Record<string, string> = {
      VOLUNTEER_AddressTaskFieldType: 'Address',
      VOLUNTEER_ShortTextTaskFieldType: 'Short Text',
      VOLUNTEER_LongTextTaskFieldType: 'Long Text',
      VOLUNTEER_NumberTaskFieldType: 'Number',
      VOLUNTEER_DateTaskFieldType: 'Date',
      VOLUNTEER_EmailTaskFieldType: 'Email',
      VOLUNTEER_PhoneTaskFieldType: 'Phone',
      VOLUNTEER_DropdownTaskFieldType: 'Dropdown',
      VOLUNTEER_MultipleChoiceTaskFieldType: 'Multiple Choice',
      VOLUNTEER_CheckboxTaskFieldType: 'Checkbox',
      VOLUNTEER_QuizTaskFieldType: 'Quiz',
      VOLUNTEER_AttachmentTaskFieldType: 'Attachment',
      VOLUNTEER_SignatureTaskFieldType: 'Signature',
    };
    return typeMap[typename] || typename;
  };

  const tasks = data?.vm?.tasks || [];

  return (
    <Box className={classes.root}>
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMore />}>
          <Typography className={classes.heading}>Available Tasks & Field IDs</Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Box width='100%'>
            {loading && <Typography>Loading tasks...</Typography>}
            {!loading && tasks.length === 0 && <Typography>No tasks found</Typography>}
            {!loading &&
              tasks.map((task: any) => {
                // Filter out removed task items and deactivated fields
                const fields =
                  task.taskItems
                    ?.filter((item: any) => item?.field && !item.dateRemoved && !item.field.dateDeactivated)
                    .map((item: any) => item.field) || [];

                // Skip tasks with no active fields
                if (fields.length === 0) {
                  return null;
                }

                return (
                  <Box key={task.taskId} mb={3}>
                    <Box display='flex' alignItems='center' mb={1}>
                      <Box flex={1}>
                        <Typography className={classes.taskTitle}>{task.title}</Typography>
                        <Box display='flex' alignItems='center'>
                          <Typography className={classes.taskId}>Task ID: {task.taskId}</Typography>
                          <Tooltip title='Copy Task ID'>
                            <IconButton className={classes.copyButton} onClick={() => copyToClipboard(task.taskId)}>
                              <FileCopy fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>

                    {fields.length > 0 && (
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>Field Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Field ID</TableCell>
                            <TableCell>Options</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields.map((field: any) => {
                            const options =
                              field.dropdownOptions ||
                              field.multipleChoiceOptions ||
                              field.checkboxOptions ||
                              field.quizTaskFieldOptions ||
                              [];

                            return (
                              <TableRow key={field.taskFieldId}>
                                <TableCell>
                                  <Typography className={classes.fieldName}>{field.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={getFieldTypeLabel(field.__typename)} size='small' />
                                </TableCell>
                                <TableCell>
                                  <Box display='flex' alignItems='center'>
                                    <Typography className={classes.fieldId}>{field.taskFieldId}</Typography>
                                    <Tooltip title='Copy Field ID'>
                                      <IconButton
                                        className={classes.copyButton}
                                        onClick={() => copyToClipboard(field.taskFieldId)}
                                      >
                                        <FileCopy fontSize='small' />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {options.length > 0 ? (
                                    <Box>
                                      {options.map((option: any) => {
                                        const optionId =
                                          option.dropdownFieldOptionId ||
                                          option.multipleChoiceFieldOptionId ||
                                          option.checkboxFieldOptionId ||
                                          option.quizTaskFieldOptionId;
                                        return (
                                          <Tooltip key={optionId} title={`ID: ${optionId}`}>
                                            <Chip
                                              className={classes.optionsChip}
                                              label={option.name}
                                              size='small'
                                              variant='outlined'
                                              onClick={() => copyToClipboard(optionId)}
                                            />
                                          </Tooltip>
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <Typography variant='body2' color='textSecondary'>
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                );
              })}
          </Box>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Box>
  );
}
